import { Box, Button, Stack, Tooltip } from '@mui/material'
import Drawer from '@mui/material/Drawer'
import { useMutation } from '@tanstack/react-query'
import { observer } from 'mobx-react'
import moment from 'moment'
import React, { FC, useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { queryClient } from '../../../../App'
import FormCheckbox from '../../../../components/Form/FormCheckbox'
import FormField from '../../../../components/Form/FormField'
import { getImageSrc } from '../../../../helpers/getImageSrc'
import { isAdmin, isOperator } from '../../../../services/AccountService'
import {
  createServiceOrder,
  createServiceSolvedOrder,
  toGuaranteeRepairOperator,
} from '../../../../services/ApiService'
import { createServiceRequestWithServiceCenter } from '../../../../services/NewApiService'
import {
  notifyError,
  notifySuccess,
} from '../../../../services/ToastifyService/ToastifyService'
import FormAutocomplete from '../../../SingleRequestPage/components/SingleRequestDrawer/elements/FormAutocomplete'
import TypedAccordion from '../../../SingleRequestPage/components/SingleRequestDrawer/elements/TypedAccordion'
import ContentTitle from './components/ContentTitle'
import DrawerHeader from './components/DrawerHeader'
import EquipmentDataAccordion from './components/EquipmentDataAccordion'
import RequestDataAccordion from './components/RequestDataAccordion'
import {
  checkDefaultValues,
  getCheckDefaultValues,
  resolver,
} from './consts/checkDrawer.consts'
import { contentWrapperSx } from './consts/styles.consts'
import { createServiceRequestStore } from './stores/NewCreateServiceRequestStore'
import {
  CheckDataForm,
  CreateServiceRequestFrom,
  DiagnosticFormType,
  ServiceCenterForm,
} from './types/NewDrawers.types'

interface NewCheckRequestDrawerProps {
  serviceStore: any
}

const NewCheckRequestDrawer: FC<NewCheckRequestDrawerProps> = ({
  serviceStore,
}) => {
  const [disableAddToServiceBtn, setDisableAddToServiceBtn] = useState(false)
  const [disableSaveAndTryLaterBtn, setDisableSaveAndTryLaterBtn] =
    useState(true)

  const {
    isCheckDrawerOpen,
    setDiagnosticData,
    setIsCheckDrawerOpen,
    setSelectedRequest,
    setCreateFormData,
    setSearchParams,
    setIsDiagnosticDrawerOpen,
    createFormData,
    diagnosticData,
    senderId,
    incidentTypeId,
    malfunctionTypeId,
    serviceCenterMalfunctionId,
    setResetAll,
    resetAll,
    setActiveServiceOrder,
    serviceCenterData,
    setServiceCenterData,
  } = createServiceRequestStore

  const defaultValues = checkDefaultValues

  const { updateCurrentServiceData, updateOperatorsTable } = serviceStore

  const methods = useForm<CheckDataForm>({
    mode: 'onChange',
    defaultValues,
    resolver,
  })

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { isValid },
  } = methods

  const { mutateAsync, isError, isPending } = useMutation({
    mutationKey: ['create-request-with-service-center'],
    mutationFn: (data: any) => createServiceRequestWithServiceCenter(data),
  })

  const { mutateAsync: toGuaranteeRepair, isPending: isToGuaranteePending } =
    useMutation({
      mutationKey: ['create-request-to-guarantee-repair'],
      mutationFn: (data: any) => toGuaranteeRepairOperator(data),
    })

  const { mutateAsync: alreadySolved, isPending: isAlreadySolvedOrderPending } =
    useMutation({
      mutationKey: ['create-already-solved-request'],
      mutationFn: (data: any) => createServiceSolvedOrder(data),
    })

  const {
    mutateAsync: createNormalOrder,
    isPending: isCreateNormalOrderPending,
  } = useMutation({
    mutationKey: ['create-normal-request'],
    mutationFn: (data: any) => createServiceOrder(data),
  })

  useEffect(() => {
    if (createFormData && diagnosticData) {
      reset(
        getCheckDefaultValues(
          createFormData as CreateServiceRequestFrom,
          diagnosticData as DiagnosticFormType,
          serviceCenterData as ServiceCenterForm,
        ),
      )
    }
  }, [createFormData, diagnosticData, serviceCenterData])

  const isEquipmentIdEmpty = createFormData?.equipmentId === 'Не заполнен'

  useEffect(() => {
    if (!isCheckDrawerOpen) {
      setDisableAddToServiceBtn(false)
      setDisableSaveAndTryLaterBtn(true)
    } else if (
      isCheckDrawerOpen &&
      (isEquipmentIdEmpty || !serviceCenterData?.serviceCenterMalfunctionId)
    ) {
      setDisableAddToServiceBtn(true)
      setDisableSaveAndTryLaterBtn(!isValid)
    }
  }, [isCheckDrawerOpen, isValid])

  useEffect(() => {
    if (
      isCheckDrawerOpen &&
      (isError ||
        isEquipmentIdEmpty ||
        !serviceCenterData?.serviceCenterMalfunctionId)
    ) {
      setDisableAddToServiceBtn(true)
      setDisableSaveAndTryLaterBtn(false)
    }
  }, [isError])

  const onReset = () => reset(defaultValues)

  const onClose = () => {
    setSelectedRequest(null)
    setCreateFormData(null)
    setDiagnosticData(null)
    setSearchParams({})
    setIsCheckDrawerOpen(false)
    setActiveServiceOrder(null)
    setServiceCenterData(null)
    setResetAll()
  }

  useEffect(() => {
    if (resetAll) {
      onReset()
    }
  }, [resetAll])

  const onSubmit = async (data: CheckDataForm) => {
    const categories = data.preferentialCategories
      .map((el, i) => !!el && i)
      .filter((el) => typeof el === 'number')

    const body = {
      ...data,
      ...data.malfunction,
      created: moment(),
      preferentialCategories: categories,
      senderId,
      incidentTypeId,
      malfunctionTypeId,
      serviceCenterMalfunctionId,
      installedOrderId: data.orderId,
      receiverSerialNumber: data.serialNumber,
      isRepeatedAppeal: watch('isRepeatedAppeal'),
    }

    const res = await mutateAsync(body)

    if (res?.id) {
      if (isAdmin()) {
        queryClient.invalidateQueries({ queryKey: ['service-table'] })
      } else {
        updateOperatorsTable()
      }
      notifySuccess('Операция выполнена успешно!')
      onClose()
      setResetAll()
    } else if (res?.validationErrors) {
      setDisableSaveAndTryLaterBtn(false)
      notifyError(res.validationErrors.join(', '))
    }
  }

  const onSaveAndCreateLater = async () => {
    const categories = watch('preferentialCategories')
      .map((el, i) => !!el && i)
      .filter((el) => typeof el === 'number')

    const body = {
      ...watch(),
      ...watch('malfunction'),
      preferentialCategories: categories,
      created: moment(),
      senderId,
      incidentTypeId,
      malfunctionTypeId,
      serviceCenterMalfunctionId,
      installedOrderId: watch('orderId'),
      receiverSerialNumber: watch('serialNumber'),
    }

    const res = await toGuaranteeRepair(body)

    if (res?.id) {
      if (isAdmin()) {
        queryClient.invalidateQueries({ queryKey: ['service-table'] })
      } else {
        updateOperatorsTable()
      }
      notifySuccess('Операция выполнена успешно!')
      onClose()
      setResetAll()
    }
  }

  const onAlreadySolvedBtnClick = async () => {
    const categories = watch('preferentialCategories')
      .map((el, i) => !!el && i)
      .filter((el) => typeof el === 'number')

    const body = {
      ...watch(),
      ...watch('malfunction'),
      preferentialCategories: categories,
      created: moment(),
      senderId,
      incidentTypeId,
      malfunctionTypeId,
      installedOrderId: watch('orderId'),
      serviceCenterMalfunctionId: null,
      receiverSerialNumber: null,
    }

    const res: any = await alreadySolved(body)

    if (res?.id) {
      if (isAdmin()) {
        queryClient.invalidateQueries({ queryKey: ['service-table'] })
      } else {
        updateOperatorsTable()
      }
      notifySuccess('Операция выполнена успешно!')
      onClose()
      setResetAll()
    }
  }

  const onCreateNormalRequest = async () => {
    const categories = watch('preferentialCategories')
      .map((el, i) => !!el && i)
      .filter((el) => typeof el === 'number')

    const body = {
      ...watch(),
      ...watch('malfunction'),
      preferentialCategories: categories,
      created: moment(),
      senderId,
      incidentTypeId,
      malfunctionTypeId,
      installedOrderId: watch('orderId'),
      serviceCenterMalfunctionId: null,
      receiverSerialNumber: null,
    }

    const res: any = await createNormalOrder(body)

    if (res?.id) {
      if (isAdmin()) {
        queryClient.invalidateQueries({ queryKey: ['service-table'] })
      } else {
        updateOperatorsTable()
      }
      notifySuccess('Операция выполнена успешно!')
      onClose()
      setResetAll()
    }
  }

  const onBack = () => {
    setIsCheckDrawerOpen(false)
    setIsDiagnosticDrawerOpen(true)
    onReset()
  }

  const isValidAndGuarantee =
    watch('incidentTypeId') === 'Неисправность по вине производителя'
  const showMalfunctionExplanation =
    watch('malfunction.malfunctionTypeId') === 'Другое'

  return (
    <Drawer
      anchor={'right'}
      open={isCheckDrawerOpen}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 380,
        },
      }}
    >
      <DrawerHeader
        title={`Заявка на ${!isOperator() ? 'обслуживание' : 'ремонт'}`}
        onBack={onBack}
        onClose={onClose}
      />
      <FormProvider {...methods}>
        <Stack
          component='form'
          onSubmit={handleSubmit(onSubmit)}
          sx={{ ...contentWrapperSx }}
          spacing={'14px'}
          justifyContent={'space-between'}
        >
          <Stack spacing={'14px'}>
            <Stack
              paddingX={'16px'}
              spacing={'14px'}
              marginBottom={'14px !important'}
            >
              <ContentTitle
                title={
                  isValidAndGuarantee
                    ? 'Шаг 4. Отправка данных в Сервис-центр'
                    : 'Шаг 4. Проверка данных и завершение'
                }
              />
              <Tooltip
                placement='top'
                title={watch('incidentTypeId')}
                disableHoverListener={!watch('incidentTypeId')}
              >
                <Box position={'relative'}>
                  <FormAutocomplete
                    control={control}
                    name='incidentTypeId'
                    fullWidth
                    label='Тип инцидента'
                    withoutTyping
                    options={[]}
                    error={!watch('incidentTypeId')}
                    fullHeight
                    disabled
                  />
                </Box>
              </Tooltip>
              {!!watch('incidentTypeId') && (
                <Tooltip
                  placement='top'
                  title={watch('malfunction.malfunctionTypeId')}
                  disableHoverListener={!watch('malfunction.malfunctionTypeId')}
                >
                  <Box position={'relative'}>
                    <FormAutocomplete
                      control={control}
                      fullWidth
                      name='malfunction.malfunctionTypeId'
                      options={[]}
                      label='Неисправность со слов Абонента'
                      error={!watch('malfunction.malfunctionTypeId')}
                      disabled
                    />
                  </Box>
                </Tooltip>
              )}
              {showMalfunctionExplanation && (
                <FormField
                  control={control}
                  name='malfunction.malfunctionExplanation'
                  fullWidth
                  label='Описание'
                  multiline
                  rows={2}
                  error={!watch('malfunction.malfunctionExplanation')}
                  disabled
                />
              )}
              <FormCheckbox
                label='Срочная заявка'
                control={control}
                name='isUrgent'
                withFireIcon
              />
              <FormCheckbox
                label='Повторное обращение'
                control={control}
                name='isRepeatedAppeal'
              />
            </Stack>
            <TypedAccordion summary={'Данные  установки'} customOpen>
              <Box
                position={'relative'}
                sx={{
                  marginTop: '16px!important',
                  img: {
                    position: 'absolute',
                    right: '20px',
                    top: '27%',
                  },
                }}
              >
                <FormField
                  control={control}
                  name='equipmentId'
                  fullWidth
                  disabled
                  label='ID оборудования'
                  onlyNumbers
                />
                {isEquipmentIdEmpty && <img src={getImageSrc('warn')} />}
              </Box>
            </TypedAccordion>
            {isValidAndGuarantee && (
              <EquipmentDataAccordion control={control} watch={watch} />
            )}
            <RequestDataAccordion />
          </Stack>
          <Stack spacing={'14px'} marginX={'16px !important'}>
            {isValidAndGuarantee && (
              <>
                <Button
                  type='submit'
                  disabled={!isValid || disableAddToServiceBtn}
                  variant='contained'
                  sx={{ paddingX: '8px !important' }}
                  loading={isPending}
                >
                  отправить заявку в сервис-центр
                </Button>
                <Button
                  variant='outlined'
                  disabled={disableSaveAndTryLaterBtn}
                  onClick={onSaveAndCreateLater}
                  loading={isToGuaranteePending}
                >
                  Сохранить и отправить позже
                </Button>
              </>
            )}
            {!isValidAndGuarantee && (
              <>
                <Button
                  type='button'
                  disabled={!isValid}
                  variant='contained'
                  sx={{ paddingX: '8px !important' }}
                  onClick={onCreateNormalRequest}
                  loading={isCreateNormalOrderPending}
                >
                  негарантийный ремонт
                </Button>
                <Button
                  variant='outlined'
                  disabled={!isValid}
                  onClick={onAlreadySolvedBtnClick}
                  loading={isAlreadySolvedOrderPending}
                >
                  Решена дистанционно
                </Button>
              </>
            )}
            <Button variant='outlined' onClick={onBack}>
              <img
                alt={'Назад'}
                src={getImageSrc('arrow-left-blue')}
                style={{ marginRight: '4px' }}
              />
              Назад
            </Button>
          </Stack>
        </Stack>
      </FormProvider>
    </Drawer>
  )
}

export default observer(NewCheckRequestDrawer)
